import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import SectionShell from "../../../component/courseDetailSection/SectionShell";
import { getCourseGlassSurfaceSx } from "../../../component/courseDetailSection/const/surface";
import type { UpToDateSkillsetCourse } from "../type";

export type UpToDateCourseDetailsSectionProps = Readonly<{
  course: UpToDateSkillsetCourse;
}>;

function getKeyPointsFromText(text: string | undefined, maxPoints: number) {
  if (!text) return [];

  const cleaned = text
    .replaceAll("：", ":")
    .replaceAll("（", "(")
    .replaceAll("）", ")")
    .replaceAll("\n", " ")
    .trim();

  if (!cleaned) return [];

  const segments = cleaned
    .split(/(?:。|；|;|\.|\!|\?|！|？)+/g)
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (!segments.length) return [];

  return segments.slice(0, maxPoints);
}

function uniqueItems(items: ReadonlyArray<string>) {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of items) {
    const trimmed = item.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }

  return result;
}

function BulletList({
  items,
}: Readonly<{
  items: string[];
}>) {
  const theme = useTheme();

  if (!items.length) return null;

  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2.25,
        listStyleType: "disc",
      }}
    >
      {items.map((item) => (
        <Typography
          key={item}
          component="li"
          variant="body2"
          sx={{
            color: alpha(theme.palette.primary.contrastText, 0.82),
            lineHeight: 1.75,
            mb: 0.75,
            "&::marker": {
              color: alpha(theme.palette.primary.contrastText, 0.82),
            },
          }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}

function DetailBlock({
  icon,
  label,
  tone,
  children,
}: Readonly<{
  icon: React.ReactNode;
  label: string;
  tone: "info" | "neutral" | "accent";
  children: React.ReactNode;
}>) {
  const theme = useTheme();

  const toneColor =
    tone === "info"
      ? alpha(theme.palette.info.light, 0.72)
      : tone === "accent"
        ? alpha(theme.palette.info.main, 0.6)
        : alpha(theme.palette.primary.contrastText, 0.22);

  return (
    <Stack
      spacing={1.25}
      sx={{
        p: { xs: 2.5, md: 3 },
        minHeight: 140,
        borderLeft: `2px solid ${toneColor}`,
        ...getCourseGlassSurfaceSx(theme),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: alpha(theme.palette.info.light, 0.92), display: "inline-flex" }}>{icon}</Box>
        <Typography variant="overline" sx={{ color: alpha(theme.palette.info.light, 0.86), letterSpacing: 0.6 }}>
          {label}
        </Typography>
      </Stack>
      {children}
    </Stack>
  );
}

export default function UpToDateCourseDetailsSection({ course }: UpToDateCourseDetailsSectionProps) {
  const theme = useTheme();
  const highlightPoints = uniqueItems(course.highlights ?? getKeyPointsFromText(course.content, 4)).slice(0, 4);
  const activityPoints = uniqueItems(course.activities ?? getKeyPointsFromText(course.activity, 6)).slice(0, 6);
  const deliverables = uniqueItems(course.deliverables ?? getKeyPointsFromText(course.activity, 4)).slice(0, 4);
  const esgPoints = uniqueItems(course.esg ?? getKeyPointsFromText(course.rebrandingFocus, 6)).slice(0, 6);

  return (
    <Stack spacing={2.5} sx={{ pb: { xs: 6, md: 9 } }}>
      <SectionShell title="課程亮點">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2.5,
          }}
        >
          <DetailBlock icon={<EmojiObjectsOutlinedIcon />} label="你會學到什麼" tone="info">
            <BulletList items={highlightPoints.length ? highlightPoints : getKeyPointsFromText(course.content, 4)} />
          </DetailBlock>

          <DetailBlock icon={<AutoAwesomeOutlinedIcon />} label="主題" tone="neutral">
            <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.9), lineHeight: 1.6 }}>
              {course.topic}
            </Typography>
          </DetailBlock>

          <DetailBlock icon={<FlagOutlinedIcon />} label="合作單位" tone="accent">
            <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.9), lineHeight: 1.6 }}>
              {course.partner}
            </Typography>
          </DetailBlock>
        </Box>
      </SectionShell>

      {course.activity ? (
        <SectionShell title="你將完成的成果">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 5fr) minmax(0, 7fr)" },
              gap: 2.5,
              alignItems: "stretch",
            }}
          >
            <DetailBlock icon={<LocalActivityOutlinedIcon />} label="Activities" tone="info">
              <BulletList items={activityPoints} />
            </DetailBlock>

            <DetailBlock icon={<AutoAwesomeOutlinedIcon />} label="Deliverables" tone="neutral">
              <BulletList items={deliverables} />
            </DetailBlock>
          </Box>
        </SectionShell>
      ) : null}

      {course.rebrandingFocus ? (
        <SectionShell title="ESG／延伸思考">
          <DetailBlock icon={<FlagOutlinedIcon />} label="Rebranding focus ESG" tone="accent">
            <BulletList items={esgPoints} />
          </DetailBlock>
        </SectionShell>
      ) : null}
    </Stack>
  );
}
